import type { AuthContextValue } from "@/contexts/AuthContext";
import { mfgScaffoldTabsEnabled } from "@/components/manufacturing/mfgFeatureFlags";
import { isFulfillmentBucketMode } from "@/lib/fulfillment/salesFloorCapability";
import { isServiceOperationalBucketMode } from "@/lib/service/serviceOperationalCapability";
import {
  SERVICE_ADMIN_ROLES,
  SERVICE_DISPATCHER_ROLES,
  SERVICE_TECH_ROLES,
} from "@/lib/roles/turboServiceRoles";
import {
  MFG_DESK_ROLES,
  MFG_PURCHASING_ROLE,
} from "@/lib/roles/turboManufacturingRoles";
import { TAB_ACCESS, type PrimaryMode } from "./leadPrimaryModeTabAccess";

export type { PrimaryMode };

// Re-export so existing `TAB_ACCESS` imports keep working.
// eslint-disable-next-line react-refresh/only-export-components
export { TAB_ACCESS };

const MFG_PILOT_MODES = [
  "mfgInventory",
  "mfgModels",
  "mfgPricing",
  "mfgOverhead",
  "mfgOrders",
  "mfgPlants",
  "mfgBuildPipeline",
  "mfgLaborBilling",
] as const;

const MFG_SCAFFOLD_BUCKET_MODES = ["mfgReports", "mfgAdminHome"] as const;
const PRODUCTION_PILOT_BUCKET_MODES = [
  "mfgAudits",
  "mfgInventoryCount",
  "mfgPurchasing",
  "mfgWasteAssistant",
  "mfgLaborPay",
  "mfgBuilderAssistant",
  "mfgQcDashboard",
] as const;
const PRODUCTION_SCAFFOLD_BUCKET_MODES = ["mfgFulfill"] as const;

const SCAFFOLD_FLAG_GATED_MODES = [
  ...MFG_SCAFFOLD_BUCKET_MODES,
  ...PRODUCTION_SCAFFOLD_BUCKET_MODES,
] as const;

const LOGISTICS_PILOT_MODES = [
  "logisticsRoutes",
  "logisticsCalendar",
  "logisticsCarriers",
] as const;

// Service offerings live in Service setup; job types in Service setup → Advanced.
// Cost rates remain a collapsible editor on Board for service_admin.
// Commercial proposal copy is Manager → Documents Editor → Proposals.
const SERVICE_PILOT_MODES = [
  "serviceBoard",
  "serviceRequests",
  "serviceScheduling",
  "serviceDefinitions",
  "serviceMaterialsUsage",
] as const;

function isMfgScaffoldFlagGatedMode(mode: PrimaryMode): boolean {
  return (SCAFFOLD_FLAG_GATED_MODES as readonly string[]).includes(mode);
}

function isFulfillmentCapabilityGatedMode(mode: PrimaryMode): boolean {
  return isFulfillmentBucketMode(mode);
}

function isServiceOperationalCapabilityGatedMode(mode: PrimaryMode): boolean {
  return isServiceOperationalBucketMode(mode);
}

// eslint-disable-next-line react-refresh/only-export-components
export const MANUFACTURING_PILOT_MODES: PrimaryMode[] = [...MFG_PILOT_MODES];
// eslint-disable-next-line react-refresh/only-export-components
export const MANUFACTURING_SCAFFOLD_MODES: PrimaryMode[] = [...MFG_SCAFFOLD_BUCKET_MODES];
// eslint-disable-next-line react-refresh/only-export-components
export const PRODUCTION_PILOT_MODES: PrimaryMode[] = [...PRODUCTION_PILOT_BUCKET_MODES];
// eslint-disable-next-line react-refresh/only-export-components
export const PRODUCTION_SCAFFOLD_MODES: PrimaryMode[] = [...PRODUCTION_SCAFFOLD_BUCKET_MODES];
// eslint-disable-next-line react-refresh/only-export-components
export const LOGISTICS_PILOT_MODE_IDS: PrimaryMode[] = [...LOGISTICS_PILOT_MODES];
// eslint-disable-next-line react-refresh/only-export-components
export const SERVICE_PILOT_MODE_IDS: PrimaryMode[] = [...SERVICE_PILOT_MODES];
// eslint-disable-next-line react-refresh/only-export-components
export { SERVICE_DISPATCHER_ROLES, SERVICE_ADMIN_ROLES, SERVICE_TECH_ROLES };

function roleMatchesList(value: string | undefined | null, allowed: string[]) {
  if (value == null || value === "" || value === "unassigned") return false;
  const allowedSet = new Set(allowed.map((a) => a.trim().toLowerCase()));
  return String(value).split(/[,/|]+/).some((part) => allowedSet.has(part.trim().toLowerCase()));
}

export function canAccessPrimaryMode(mode: PrimaryMode, auth: AuthContextValue): boolean {
  if (isMfgScaffoldFlagGatedMode(mode) && !mfgScaffoldTabsEnabled()) return false;
  if (isFulfillmentCapabilityGatedMode(mode) && !auth.hasSalesFloor) return false;
  if (isServiceOperationalCapabilityGatedMode(mode) && !auth.servicesEnabled) return false;

  // Purchasing-only seat is Manufacturing Inventory + Models — not the open
  // Sales modes (`leads` / `salesConsole` use null TAB_ACCESS = everyone).
  if (
    auth.hasRole(MFG_PURCHASING_ROLE) &&
    !auth.hasRole(...MFG_DESK_ROLES) &&
    !auth.hasRole("sales", "sales_rep") &&
    (mode === "leads" || mode === "salesConsole")
  ) {
    return false;
  }

  const required = TAB_ACCESS[mode];
  if (!required) return true;
  if (!auth.session) return false;
  if (auth.hasRole(...required)) return true;
  if (roleMatchesList(auth.role, required)) return true;
  if (auth.platformRole && roleMatchesList(auth.platformRole, required)) return true;
  return false;
}
